"use client";

import { useState } from "react";
import Image from "next/image";
import { isAuthenticated, decodeUserIdFromToken } from "@/lib/auth";
import { ApiPost } from "@/lib/api";
import { supabase } from "@/lib/supabase";
import { useHasActiveCoupon } from "@/lib/use-coupon";
import * as Select from "@radix-ui/react-select";
import { ChevronDown } from "lucide-react";
import Flag from "react-world-flags";

const CountryConfig = [
  { label: "US", value: "US" },
  { label: "UK", value: "GB" },
  { label: "DE", value: "DE" },
  { label: "FR", value: "FR" },
  { label: "JP", value: "JP" },
  { label: "CA", value: "CA" },
  { label: "CN", value: "CN" },
  { label: "IT", value: "IT" },
  { label: "ES", value: "ES" },
  { label: "IN", value: "IN" },
  { label: "MX", value: "MX" },
  { label: "BR", value: "BR" },
  { label: "AU", value: "AU" },
] as const;

const heroFont = { fontFamily: "var(--font-display), 'Hanken Grotesk', sans-serif" };

export default function HeroSection() {
  const [asin, setAsin] = useState("");
  const [country, setCountry] = useState("US");
  const [inputError, setInputError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  type CustomerAvatarRecord = {
    id: string;
    status: string;
    created_at: string;
    "creative-brief"?: { status: string };
  };

  const [existingAvatar, setExistingAvatar] = useState<CustomerAvatarRecord | null>(null);
  const hasCoupon = useHasActiveCoupon();

  const isASIN = (val: string) => /^[A-Z0-9]{10}$/.test(val);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.toUpperCase();
    setAsin(val);
    if (val.length === 0) {
      setInputError("");
    } else if (!isASIN(val)) {
      setInputError("Enter a valid ASIN (10 characters)");
    } else {
      setInputError("");
    }
  };

  const handleGenerate = async () => {
    if (hasCoupon) {
      window.location.href = "/pricing";
      return;
    }
    if (!asin.trim() || !isASIN(asin)) {
      setInputError("Enter a valid ASIN");
      return;
    }

    if (!isAuthenticated()) {
      const targetPath = `/customer-brief?asin=${encodeURIComponent(asin.trim())}&country=${country === "GB" ? "UK" : country}`;
      window.location.href = `/login?redirect=${encodeURIComponent(targetPath)}`;
      return;
    }

    const userId = decodeUserIdFromToken();
    if (!userId) {
      window.location.href = `/login`;
      return;
    }

    const code = country === "GB" ? "UK" : country;
    setIsLoading(true);

    try {
      // Check for existing campaign
      const { data: existingData } = await supabase
        .from("customer_avatar")
        .select('*, "creative-brief"(id,status)')
        .eq("assigned_to", userId)
        .eq("asin", asin.trim())
        .eq("country", code)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (existingData) {
        setExistingAvatar(existingData as CustomerAvatarRecord);
        setShowDuplicateModal(true);
        setIsLoading(false);
        return;
      }

      const response = await ApiPost<{ inserted_id?: string; avatar_id?: string; status_code?: number; detail?: string }>(
        '/generate-avatars-for-asin',
        {
          asin: asin.trim(),
          id: userId,
          country: code,
          generate_main_images: true,
          generate_secondary_images: true,
          generate_a_plus_images: true,
        }
      );

      if (response.status_code === 500) {
        setInputError(response.detail || "Session expired. Please login again.");
        setIsLoading(false);
        return;
      }

      const campaignId = response.inserted_id || response.avatar_id;
      if (campaignId) {
        window.location.href = `/campaigns/${campaignId}`;
      } else {
        setInputError("Failed to create campaign. Please try again.");
      }
    } catch (error) {
      console.error('[DEBUG] Generate error:', error);
      const err = error as { response?: { data?: { detail?: string } } };
      setInputError(err.response?.data?.detail || "Failed to generate. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleGenerate();
    }
  };

  const handleViewExisting = () => {
    if (existingAvatar) {
      window.location.href = `/campaigns/${existingAvatar.id}`;
    }
    setShowDuplicateModal(false);
  };

  const handleStartNew = async () => {
    if (!existingAvatar) return;

    const userId = decodeUserIdFromToken();
    if (!userId) {
      window.location.href = `/login`;
      return;
    }

    const code = country === "GB" ? "UK" : country;
    setShowDuplicateModal(false);
    setIsLoading(true);

    try {
      const response = await ApiPost<{ inserted_id?: string; avatar_id?: string; status_code?: number; detail?: string }>(
        '/generate-avatars-for-asin',
        {
          asin: asin.trim(),
          id: userId,
          country: code,
          generate_main_images: true,
          generate_secondary_images: true,
          generate_a_plus_images: true,
        }
      );

      if (response.status_code === 500) {
        setInputError(response.detail || "Session expired. Please login again.");
        setIsLoading(false);
        return;
      }

      const campaignId = response.inserted_id || response.avatar_id;
      if (campaignId) {
        window.location.href = `/campaigns/${campaignId}`;
      } else {
        setInputError("Failed to create campaign. Please try again.");
      }
    } catch (error) {
      console.error('[DEBUG] Generate error:', error);
      const err = error as { response?: { data?: { detail?: string } } };
      setInputError(err.response?.data?.detail || "Failed to generate. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Brief may come back as an array (one-to-many) or object depending on the relationship.
  const briefData = existingAvatar?.["creative-brief"];
  const briefStatus: string | undefined = Array.isArray(briefData)
    ? briefData[0]?.status
    : briefData?.status;

  // Campaign is still running if:
  //   - avatar is pending (not yet analysed), OR
  //   - avatar is done but brief hasn't completed yet
  const isProcessing =
    existingAvatar?.status === "pending" ||
    (existingAvatar?.status === "completed" &&
      briefStatus !== "completed" &&
      briefStatus !== "failed");

  return (
    <section id="hero" className="flex relative bg-white overflow-hidden pt-[64px] md:max-h-[1000px] md:h-[100vh]  md:bg-[url('/hero-bg.png')] bg-no-repeat bg-cover">
      <div className="flex flex-1 md:flex-row md:mt-[-64px] items-center h-full relative">
        <div className="w-full h-full min-h-[400px] md:min-h-[700px] pl-[5%] sm:pl-[6%] md:pl-[10%] pr-[5%] pb-[5%] md:pb-0 flex flex-col justify-center relative z-10">
          <h1
            className="text-[#1B1C30] mb-4 mt-20 animate-slide-up"
            style={{ ...heroFont, fontSize: "clamp(30px, 4vw, 48px)", fontWeight: 700, lineHeight: 1.1, letterSpacing: "-0.03em" }}
          >
            Generate high-converting Amazon images and A+ content in minutes.
          </h1>

          <p
            className="text-[#5C5F73] mb-8 animate-slide-up animation-delay-100"
            style={{ ...heroFont, fontSize: 16, fontWeight: 400, lineHeight: 1.5 }}
          >
            Using AI trained on thousands of winning listings.
          </p>

          <div className="animate-slide-up animation-delay-200 sm:max-w-[640px] md:max-w-none">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full">
              <div className="flex items-center flex-1 bg-white border border-[#D8DDE8] rounded-full overflow-visible">
                <Select.Root value={country} onValueChange={setCountry}>
                  <Select.Trigger className="flex items-center gap-1 pl-4 pr-3 border-r border-[#E4E7EF] h-full cursor-pointer bg-transparent border-none outline-none">
                    <span className="w-7 h-5 overflow-hidden rounded-sm">
                      <Flag code={country} className="w-full h-full object-cover" />
                    </span>
                    <span className="text-[13px] font-medium text-[#1B1C30]">
                      {country === "GB" ? "UK" : country}
                    </span>
                    <Select.Icon>
                      <ChevronDown className="w-3 h-3 text-[#8B8FA3]" />
                    </Select.Icon>
                  </Select.Trigger>
                  <Select.Portal>
                    <Select.Content className="bg-white border border-gray-200 rounded-md shadow-lg z-50 min-w-[120px] max-h-64 overflow-y-auto" position="popper" sideOffset={4}>
                      <Select.Viewport className="p-1">
                        {CountryConfig.map((c) => (
                          <Select.Item key={c.value} value={c.value} className="flex items-center gap-2 px-3 py-2 text-sm text-gray-900 hover:bg-gray-50 cursor-pointer rounded-sm outline-none">
                            <span className="w-9 h-6 inline-block overflow-hidden rounded-sm">
                              <Flag code={c.value} className="w-full h-full object-cover" />
                            </span>
                            <Select.ItemText>{c.label}</Select.ItemText>
                          </Select.Item>
                        ))}
                      </Select.Viewport>
                    </Select.Content>
                  </Select.Portal>
                </Select.Root>

                <input
                  type="text"
                  placeholder="Enter Amazon ASIN"
                  value={asin}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  required
                  minLength={10}
                  maxLength={10}
                  pattern="^[A-Z0-9]{10}$"
                  aria-invalid={!!inputError}
                  className="flex-1 px-4 h-full text-[16px] text-[#1B1C30] placeholder:text-[#A0A4B8] outline-none bg-transparent"
                  style={{ ...heroFont, height: 56 }}
                />
              </div>

              <button
                onClick={handleGenerate}
                disabled={isLoading || !!inputError}
                className="bg-[#1464FF] hover:bg-[#0D4FD6] disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold rounded-full transition-colors whitespace-nowrap flex-shrink-0 h-14 px-8 sm:px-10 sm:h-[56px] flex items-center justify-center gap-2 w-full sm:w-auto"
                style={{ ...heroFont, fontSize: 16 }}
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Loading...
                  </>
                ) : (
                  "Generate"
                )}
              </button>
            </div>

            {inputError && (
              <p className="mt-2 text-sm text-red-600" style={heroFont}>{inputError}</p>
            )}

            <div className="mt-6 flex w-full max-w-[311px] flex-col items-start gap-4 font-display">
              <div className="flex h-6 items-center gap-2 text-[#4D4D5C]">
                <Image
                  src="/everything/admin_panel_settings.svg"
                  alt=""
                  className="h-6 w-6 flex-none"
                  width={24}
                  height={24}
                  unoptimized
                  aria-hidden="true"
                />
                <p className="text-sm leading-[18px]">
                  Trusted by 1000+ Brands and 100+ Agencies
                </p>
              </div>
              <div className="flex h-6 items-center gap-2 text-[#4D4D5C]">
                <Image
                  src="/everything/credit_card_off.svg"
                  alt=""
                  className="h-6 w-6 flex-none"
                  width={24}
                  height={24}
                  unoptimized
                  aria-hidden="true"
                />
                <p className="text-sm leading-[18px]">
                  Free Trial, No credit card needed. try now
                </p>
              </div>
            </div>

          </div>
        </div>
      </div>
            <div className="flex-1 hidden md:flex">&nbsp;</div>
      {/* Duplicate ASIN Dialog */}
      {showDuplicateModal && existingAvatar && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowDuplicateModal(false)} />
          <div className="relative bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-[#1B1C30] mb-2" style={heroFont}>
              {isProcessing ? "Campaign In Progress" : "Campaign Already Exists"}
            </h3>
            <p className="text-sm text-[#5C5F73] mb-6" style={heroFont}>
              {isProcessing
                ? `A campaign for this ASIN is currently being processed${existingAvatar.created_at ? ` (started on ${new Date(existingAvatar.created_at).toLocaleString()})` : ""}. Please wait and view the result.`
                : `This ASIN already has a campaign${existingAvatar.created_at ? ` (created on ${new Date(existingAvatar.created_at).toLocaleString()})` : ""}. Do you want to view the existing campaign or start a new one?`}
            </p>
            <div className="flex gap-3 justify-end">
              {!isProcessing && (
                <button
                  onClick={handleStartNew}
                  className="px-6 py-2 text-sm font-medium border border-[#D8DDE8] rounded-full hover:bg-[#F5F6FA] transition-colors text-[#1B1C30]"
                  style={heroFont}
                >
                  Start New
                </button>
              )}
              <button
                onClick={handleViewExisting}
                className="px-6 py-2 text-sm font-semibold bg-[#1464FF] text-white rounded-full hover:bg-[#0D4FD6] transition-colors"
                style={heroFont}
              >
                View Existing
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
